import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Copy, Check, Loader2 } from "lucide-react";
import { FoundingMakerBadge } from "@/components/FoundingMakerBadge";

type ReferralCode = { code: string; shareUrl: string };

function buildMessage(code: string): string {
  return `I just joined Stoop, a new local marketplace for the 518. My first 10 customers get FREE delivery on their first 2 orders ($15+) plus an extra discount when they use my code. Order from me here: trystoop.com/install?ref=${code}`;
}

export function FoundingMakerShareKit() {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);

  const { data: referralCode } = useQuery<ReferralCode>({
    queryKey: ["/api/my/referral-code"],
  });

  const code = referralCode?.code ?? null;

  const handleCopy = () => {
    if (!code) {
      toast({ title: "Still loading — try again in a sec" });
      return;
    }
    // Clipboard write must run inside the synchronous click gesture (trap 11).
    navigator.clipboard
      .writeText(buildMessage(code))
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {
        toast({ title: "Couldn't copy", variant: "destructive" });
      });
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <FoundingMakerBadge size="sm" />
        </div>
        <CardTitle className="text-base">Share your founder invite</CardTitle>
        <CardDescription>
          Your first 10 customers get free delivery on their first 2 orders ($15+) when they use
          your code. Post this anywhere to bring them in.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="rounded-md border bg-muted/30 p-3 text-sm leading-relaxed">
          {code ? (
            buildMessage(code)
          ) : (
            <span className="inline-flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading your code…
            </span>
          )}
        </div>
        <Button onClick={handleCopy} className="w-full" disabled={!code}>
          {copied ? <Check className="mr-2 h-4 w-4" /> : <Copy className="mr-2 h-4 w-4" />}
          {copied ? "Copied" : "Copy message"}
        </Button>
        {code && (
          <p className="text-center text-xs text-muted-foreground">
            Your code: <span className="font-mono font-semibold">{code}</span>
          </p>
        )}
      </CardContent>
    </Card>
  );
}
